import React, { useEffect, useState } from 'react';
import { 
  Wifi, 
  WifiOff, 
  Battery, 
  Signal,
  MapPin,
  Calendar,
  Wrench,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock
} from 'lucide-react';
import { iotApi, IoTNode } from '../lib/iotApi';
import { IS_AWS_MODE } from '../config/frontend';

interface Device {
  id: string;
  name: string;
  type: 'gateway' | 'valve_node' | 'motor_node' | 'sensor_node';
  model: string;
  serial: string;
  firmware: string;
  status: 'online' | 'offline' | 'maintenance' | 'warning';
  last_seen: string;
  location: string;
  zone?: string;
  battery_level?: number;
  signal_strength?: number;
  uptime?: string;
  data?: {
    temperature?: number;
    humidity?: number;
    pressure?: number;
    flow_rate?: number;
    valve_position?: number;
  };
}

const Devices: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [awsNodes, setAwsNodes] = useState<IoTNode[]>([]);

  useEffect(() => {
    if (!IS_AWS_MODE) return;
    iotApi.get('/iot/nodes').then(setAwsNodes).catch(console.error);
  }, []);

  const demoDevices: Device[] = [
    {
      id: 'gw-001',
      name: 'Gateway Vignes Central',
      type: 'gateway',
      model: 'AgriGW-Pro',
      serial: 'GW001-2024-001',
      firmware: '2.1.4',
      status: 'online',
      last_seen: '2024-01-15T14:30:00Z',
      location: 'Domaine Les Vignes',
      battery_level: 98,
      signal_strength: 95,
      uptime: '45d 12h 30m',
      data: {
        temperature: 22.5,
        humidity: 65
      }
    },
    {
      id: 'vn-001',
      name: 'Valve Zone A-1',
      type: 'valve_node',
      model: 'SmartValve-200',
      serial: 'VN001-2024-002',
      firmware: '1.8.2',
      status: 'online',
      last_seen: '2024-01-15T14:28:00Z',
      location: 'Field A North',
      zone: 'Zone A-1',
      battery_level: 87,
      signal_strength: 82,
      uptime: '32d 8h 15m',
      data: {
        pressure: 3.2,
        valve_position: 75
      }
    },
    {
      id: 'vn-002',
      name: 'Valve Zone A-2',
      type: 'valve_node',
      model: 'SmartValve-200',
      serial: 'VN002-2024-003',
      firmware: '1.8.2',
      status: 'warning',
      last_seen: '2024-01-15T14:15:00Z',
      location: 'Field A South',
      zone: 'Zone A-2',
      battery_level: 23,
      signal_strength: 45,
      uptime: '28d 4h 22m',
      data: {
        pressure: 2.1,
        valve_position: 0
      }
    },
    {
      id: 'mn-001',
      name: 'Pump Station Alpha',
      type: 'motor_node',
      model: 'PowerPump-1000',
      serial: 'MN001-2024-004',
      firmware: '3.2.1',
      status: 'offline',
      last_seen: '2024-01-15T12:45:00Z',
      location: 'Central Pump House',
      battery_level: 0,
      signal_strength: 0,
      uptime: '0d 0h 0m',
      data: {
        pressure: 0,
        flow_rate: 0
      }
    },
    {
      id: 'sn-001',
      name: 'Moisture Sensor A-1-01',
      type: 'sensor_node',
      model: 'SoilSense-Pro',
      serial: 'SN001-2024-005',
      firmware: '1.4.7',
      status: 'online',
      last_seen: '2024-01-15T14:29:00Z',
      location: 'Zone A-1 Center',
      zone: 'Zone A-1',
      battery_level: 76,
      signal_strength: 88,
      uptime: '18d 16h 45m',
      data: {
        temperature: 18.2,
        humidity: 68
      }
    },
    {
      id: 'sn-002',
      name: 'Moisture Sensor B-1-01',
      type: 'sensor_node',
      model: 'SoilSense-Pro',
      serial: 'SN002-2024-006',
      firmware: '1.4.6',
      status: 'maintenance',
      last_seen: '2024-01-15T09:30:00Z',
      location: 'Zone B-1 North',
      zone: 'Zone B-1',
      battery_level: 45,
      signal_strength: 0,
      uptime: '0d 0h 0m',
      data: {
        temperature: 0,
        humidity: 0
      }
    }
  ];

  const devices: Device[] = IS_AWS_MODE
    ? awsNodes.map<Device>((node) => ({
        id: node.id,
        name: node.name,
        type: node.model === 'valve_water_flow'
          ? 'valve_node'
          : node.model === 'relay_4ch'
            ? 'motor_node'
            : 'sensor_node',
        model: node.model,
        serial: node.dev_eui,
        firmware: 'TTN',
        status: node.last_seen_at ? 'online' : 'offline',
        last_seen: node.last_seen_at || '',
        location: node.sector_name || 'AWS DEV',
        zone: node.sector_name || undefined,
      }))
    : demoDevices;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'var(--color-success)';
      case 'warning': return 'var(--color-warning)';
      case 'offline': return 'var(--color-danger)';
      case 'maintenance': return 'var(--color-info)';
      default: return 'var(--text-secondary)';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return CheckCircle;
      case 'warning': return AlertTriangle;
      case 'offline': return XCircle;
      case 'maintenance': return Wrench;
      default: return Clock;
    }
  };

  const getDeviceTypeLabel = (type: string) => {
    switch (type) {
      case 'gateway': return 'Gateway';
      case 'valve_node': return 'Valve Node';
      case 'motor_node': return 'Motor Node';
      case 'sensor_node': return 'Sensor Node';
      default: return type;
    }
  };

  const getDeviceTypeIcon = (type: string) => {
    switch (type) {
      case 'gateway': return '📡';
      case 'valve_node': return '💧';
      case 'motor_node': return '⚡';
      case 'sensor_node': return '📊';
      default: return '🔧';
    }
  };

  const getBatteryColor = (level: number) => {
    if (level > 50) return 'var(--color-success)';
    if (level > 20) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  const getSignalColor = (strength: number) => {
    if (strength > 70) return 'var(--color-success)';
    if (strength > 40) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  const filteredDevices = devices.filter(device => {
    const statusMatch = filter === 'all' || device.status === filter;
    const typeMatch = selectedType === 'all' || device.type === selectedType;
    return statusMatch && typeMatch;
  });

  const getTimeAgo = (timestamp: string) => {
    const now = new Date();
    const lastSeen = new Date(timestamp);
    const diffMs = now.getTime() - lastSeen.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-heading-2 text-[var(--text-primary)]">IoT Device Inventory</h1>
          <p className="text-body text-[var(--text-secondary)] mt-1">
            Monitor and manage all connected devices across your irrigation network
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">Type:</label>
            <select 
              value={selectedType} 
              onChange={(e) => setSelectedType(e.target.value)}
              className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
              data-testid="device-type-filter"
            >
              <option value="all">All Types</option>
              <option value="gateway">Gateway</option>
              <option value="valve_node">Valve Nodes</option>
              <option value="motor_node">Motor Nodes</option>
              <option value="sensor_node">Sensor Nodes</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-[var(--text-primary)]">Status:</label>
            <select 
              value={filter} 
              onChange={(e) => setFilter(e.target.value)}
              className="text-sm border border-[var(--border-light)] rounded-lg px-3 py-2 bg-[var(--surface)]"
              data-testid="device-status-filter"
            >
              <option value="all">All Status</option>
              <option value="online">Online</option>
              <option value="warning">Warning</option>
              <option value="offline">Offline</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Devices', value: devices.length, color: 'var(--color-primary)' },
          { label: 'Online', value: devices.filter(d => d.status === 'online').length, color: 'var(--color-success)' },
          { label: 'Warnings', value: devices.filter(d => d.status === 'warning').length, color: 'var(--color-warning)' },
          { label: 'Offline', value: devices.filter(d => d.status === 'offline').length, color: 'var(--color-danger)' }
        ].map((stat, index) => (
          <div key={index} className="card text-center">
            <div 
              className="text-2xl font-bold mb-1"
              style={{ color: stat.color }}
            >
              {stat.value}
            </div>
            <div className="text-sm text-[var(--text-secondary)]">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Device Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredDevices.map((device) => {
          const StatusIcon = getStatusIcon(device.status);
          
          return (
            <div 
              key={device.id}
              className="card hover:shadow-lg transition-all"
              data-testid={`device-card-${device.id}`}
            >
              {/* Device Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className="text-2xl">{getDeviceTypeIcon(device.type)}</div>
                  <div>
                    <h3 className="text-heading-4 text-[var(--text-primary)]">{device.name}</h3>
                    <p className="text-body-sm text-[var(--text-secondary)]">
                      {getDeviceTypeLabel(device.type)}
                    </p>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      {device.model} • {device.serial}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-1">
                  <StatusIcon 
                    className="w-5 h-5" 
                    style={{ color: getStatusColor(device.status) }}
                  />
                  <span 
                    className="px-2 py-1 text-xs font-medium rounded-full text-white capitalize"
                    style={{ backgroundColor: getStatusColor(device.status) }}
                  >
                    {device.status}
                  </span>
                </div>
              </div>

              {/* Location & Zone */}
              <div className="mb-4 space-y-2">
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-[var(--text-secondary)]" />
                  <span className="text-sm text-[var(--text-primary)]">{device.location}</span>
                </div>
                {device.zone && (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-[var(--color-primary)] rounded-sm flex items-center justify-center">
                      <span className="text-xs text-white font-bold">Z</span>
                    </div>
                    <span className="text-sm text-[var(--text-primary)]">{device.zone}</span>
                  </div>
                )}
              </div>

              {/* System Info */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center p-2 bg-[var(--gray-50)] rounded-lg">
                  <div className="text-sm font-semibold text-[var(--text-primary)]">
                    v{device.firmware}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)]">Firmware</div>
                </div>
                
                <div className="text-center p-2 bg-[var(--gray-50)] rounded-lg">
                  <div className="text-sm font-semibold text-[var(--text-primary)]">
                    {device.uptime || '0d 0h 0m'}
                  </div>
                  <div className="text-xs text-[var(--text-secondary)]">Uptime</div>
                </div>
              </div>

              {/* Battery & Signal */}
              {device.status !== 'offline' && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Battery 
                      className="w-4 h-4" 
                      style={{ color: getBatteryColor(device.battery_level || 0) }}
                    />
                    <div className="flex-1">
                      <div className="text-xs text-[var(--text-secondary)]">Battery</div>
                      <div 
                        className="text-sm font-medium"
                        style={{ color: getBatteryColor(device.battery_level || 0) }}
                      >
                        {device.battery_level || 0}%
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Signal 
                      className="w-4 h-4" 
                      style={{ color: getSignalColor(device.signal_strength || 0) }}
                    />
                    <div className="flex-1">
                      <div className="text-xs text-[var(--text-secondary)]">Signal</div>
                      <div 
                        className="text-sm font-medium"
                        style={{ color: getSignalColor(device.signal_strength || 0) }}
                      >
                        {device.signal_strength || 0}%
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Sensor Data */}
              {device.data && Object.keys(device.data).length > 0 && (
                <div className="mb-4 p-3 bg-[var(--gray-50)] rounded-lg">
                  <div className="text-xs font-medium text-[var(--text-primary)] mb-2">Current Readings</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {device.data.temperature && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Temp:</span>
                        <span className="font-medium">{device.data.temperature}°C</span>
                      </div>
                    )}
                    {device.data.humidity && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Humidity:</span>
                        <span className="font-medium">{device.data.humidity}%</span>
                      </div>
                    )}
                    {device.data.pressure && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Pressure:</span>
                        <span className="font-medium">{device.data.pressure} bar</span>
                      </div>
                    )}
                    {device.data.flow_rate && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Flow:</span>
                        <span className="font-medium">{device.data.flow_rate} m³/h</span>
                      </div>
                    )}
                    {device.data.valve_position !== undefined && (
                      <div className="flex justify-between">
                        <span className="text-[var(--text-secondary)]">Valve:</span>
                        <span className="font-medium">{device.data.valve_position}%</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Last Seen */}
              <div className="flex items-center justify-between text-xs text-[var(--text-secondary)]">
                <div className="flex items-center space-x-1">
                  <Clock className="w-3 h-3" />
                  <span>Last seen: {getTimeAgo(device.last_seen)}</span>
                </div>
                
                <button 
                  className="text-[var(--color-primary)] hover:text-[var(--color-primary-700)] font-medium"
                  data-testid={`device-details-${device.id}`}
                >
                  Details
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredDevices.length === 0 && (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📱</div>
          <h3 className="text-heading-4 text-[var(--text-primary)] mb-2">
            No devices found
          </h3>
          <p className="text-body text-[var(--text-secondary)]">
            Try adjusting your filters to see more devices
          </p>
        </div>
      )}
    </div>
  );
};

export default Devices;
